import { TablePlaceHolder } from "@app/components/PlaceHolder";
import { CarPageContent } from "@app/components/PageContent";
import { CarPageContentCode, usePageContent } from "@app/hooks/usePageContent";
import { useEffect } from "react";
import { usePortfolioGroupParams } from "@app/hooks/usePortfolioGroupParams";
import { usePortfolioGroupVersionModules } from "../usePortfolioGroupVersionModules";
import { AnalyzeSummary } from "./analyze/AnalyzeSummary";
import { CarSelectOptionField, CarSelectOption } from "@app/components/Inputs";
import { AnalyzeModule } from "./analyze/AnalyzeModule";
import { useLocalStorage } from "@app/hooks/useLocalStorage";
import { CarTooltipBox } from "@app/components/TooltipBox";
import { pendoClasses } from "@app/core/thirdParty/pendo";
import { usePortfolioGroupInfo } from "../usePortfolioGroupInfo";
import { Typography } from "@mui/material";

export const EditPortfolioGroupAnalyze = (props: { isReadOnly?: boolean }) => {
  const pageContent = usePageContent();

  const [reportFocus, setReportFocus] = useLocalStorage(
    "EditPortfolioGroupAnalyze_reportFocus",
    "summary",
  );

  const {
    portfolioGroupId,
    portfolioGroupVersionId,
    isViewPortfolioGroupVersionMode,
  } = usePortfolioGroupParams();

  const groupInfo = usePortfolioGroupInfo({
    portfolioGroupId,
  });

  const versionModules = usePortfolioGroupVersionModules({
    portfolioGroupVersionId: isViewPortfolioGroupVersionMode
      ? portfolioGroupVersionId
      : groupInfo.portfolioGroupVersionId,
    hideCash: true,
  });

  const moduleItems = versionModules.items.map<CarSelectOption<string>>(
    (i) => ({
      label: i.title,
      value: i.id ?? "",
    }),
  );

  const shouldSwitchToSummary =
    reportFocus !== "summary" &&
    !versionModules.isLoading &&
    !moduleItems.some((i) => i.value === reportFocus);

  useEffect(() => {
    if (shouldSwitchToSummary) {
      setReportFocus("summary");
    }
  }, [shouldSwitchToSummary, setReportFocus]);

  const reportFocusItems: CarSelectOption<string>[] = [
    {
      label: "Summary",
      value: "summary",
    },
    ...moduleItems,
  ];

  const reportFocusSelector = (
    <CarSelectOptionField
      sx={{
        width: 200,

        ".MuiInputLabel-root": {
          fontSize: "16px",
        },
        ".MuiOutlinedInput-root": {
          mt: 3,
        },
        ".MuiInputBase-input": {
          backgroundColor: "gray1",
        },
      }}
      label={
        <>
          Report Focus
          <CarTooltipBox
            sx={{ display: "inline-block", ml: 0.5, mb: -0.5 }}
            className={pendoClasses.editPortfolioAnalyzeReportFocus}
          />
        </>
      }
      options={reportFocusItems}
      value={reportFocus}
      onChange={(value) => {
        if (value) {
          setReportFocus(value);
        }
      }}
    />
  );

  return (
    <TablePlaceHolder
      isLoading={versionModules.isLoading || pageContent.isLoading}
      rows={8}
      sx={{ mt: 5 }}
    >
      <Typography variant="h2SSemiBold" sx={{ mt: 4 }}>
        Analyze
      </Typography>

      <CarPageContent
        sx={{ mt: 3 }}
        content={pageContent.getContent(
          groupInfo.isSinglePortfolioMode
            ? CarPageContentCode.PORTFOLIO_ANALYSIS_ANALYZE_OVERVIEW
            : CarPageContentCode.ANALYZE_PORTFOLIO_GROUP_REPORTS_MODULE,
        )}
      />
      {groupInfo.isSinglePortfolioMode ? (
        <AnalyzeModule
          moduleId={moduleItems.at(0)?.value ?? ""}
          moduleIdx={0}
          reportFocusSelector={<></>}
        />
      ) : reportFocus === "summary" ? (
        <AnalyzeSummary reportFocusSelector={reportFocusSelector} />
      ) : (
        <AnalyzeModule
          moduleId={reportFocus}
          moduleIdx={moduleItems.findIndex((i) => i.value === reportFocus)}
          reportFocusSelector={reportFocusSelector}
          isReadOnly={props.isReadOnly}
        />
      )}
    </TablePlaceHolder>
  );
};
